import { MY_TASKS_SOURCE } from "@jugl-web/domain-resources/tasks";
import { TaskCard } from "@jugl-web/domain-resources/tasks/components/TaskCard";
import { CustomerTaskCardProvider } from "@jugl-web/domain-resources/tasks/providers/TaskCardProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { CustomerTaskModel } from "@jugl-web/rest-api/tasks/models/CustomerTask";
import {
  DataLoadingWrapper,
  inputValueToCustomerPhoneString,
  phoneStringToInputValue,
} from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { VList, VListHandle } from "virtua";
import { useClientSupportPageWithSelectedConversation } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageWithSelectedConversation";

type PageState = {
  pageNumber: number;
  requestId: number;
  isError?: boolean;
  hasMore?: boolean;
};

export const TasksTabContent: FC = () => {
  const { t } = useTranslations();
  const { tasksApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { navigateToPage } = useNavigation();
  const { selectedConversation } =
    useClientSupportPageWithSelectedConversation();
  const currentPageState = useRef<PageState | null>(null);
  const [customerTasks, setCustomerTasks] = useState<CustomerTaskModel[]>([]);
  const vListRef = useRef<VListHandle>(null);

  const [
    getCustomerTasks,
    {
      isLoading: isCustomerTasksLoading,
      isFetching: isCustomerTasksFetching,
      isError: isCustomerTasksError,
    },
  ] = tasksApi.useLazyGetCustomerTasksQuery();

  const loadNextPage = useCallback(async () => {
    if (!selectedConversation.conv_id) {
      return;
    }

    let nextPageNumber = 1;
    if (currentPageState.current) {
      if (currentPageState.current.isError) {
        nextPageNumber = currentPageState.current.pageNumber;
      } else {
        nextPageNumber = currentPageState.current.pageNumber + 1;
      }
    }

    const nextPageState: PageState = {
      requestId: Math.random(),
      pageNumber: nextPageNumber,
    };

    currentPageState.current = nextPageState;

    const result = await getCustomerTasks({
      entityId,
      params: {
        phone_number: inputValueToCustomerPhoneString(
          phoneStringToInputValue(`+${selectedConversation.conv_id}`)
        ),
        page: nextPageNumber,
        page_size: 10,
      },
    });

    if (currentPageState.current?.requestId !== nextPageState.requestId) {
      return;
    }

    if ("error" in result && result.error) {
      nextPageState.isError = true;
      currentPageState.current = nextPageState;
      return;
    }

    if ("data" in result && result.data && result.data.data) {
      const responseData = result.data.data;

      setCustomerTasks((prev) =>
        nextPageNumber === 1 ? responseData : [...prev, ...responseData]
      );

      nextPageState.hasMore = result.data.total_pages > nextPageNumber;
      currentPageState.current = nextPageState;
    }
  }, [entityId, getCustomerTasks, selectedConversation.conv_id]);

  useEffect(() => {
    currentPageState.current = null;
    setCustomerTasks([]);
    loadNextPage();
  }, [selectedConversation.conv_id, loadNextPage]);

  const handleVListOnScroll = useCallback(
    (offset: number) => {
      if (!vListRef.current) {
        return;
      }
      if (!currentPageState.current?.hasMore || isCustomerTasksLoading) {
        return;
      }
      const bottomOffset =
        offset - vListRef.current.scrollSize + vListRef.current.viewportSize;

      if (bottomOffset > -150) {
        loadNextPage();
      }
    },
    [loadNextPage, isCustomerTasksLoading]
  );

  return (
    <DataLoadingWrapper
      isLoading={
        isCustomerTasksLoading ||
        (isCustomerTasksFetching && currentPageState.current?.pageNumber === 1)
      }
      isFetching={isCustomerTasksFetching}
      isError={isCustomerTasksError}
      onRetry={loadNextPage}
    >
      {customerTasks.length ? (
        <VList
          overscan={10}
          onScroll={handleVListOnScroll}
          ref={vListRef}
          className="jugl__custom-scrollbar px-6 py-[34px]"
        >
          {customerTasks.map((customerTask, idx) => (
            <CustomerTaskCardProvider
              key={customerTask.id}
              entityId={entityId}
              source={MY_TASKS_SOURCE}
              task={customerTask}
            >
              {(props) => (
                <TaskCard
                  onClick={() =>
                    navigateToPage("taskDetails", {
                      taskId: customerTask.id,
                    })
                  }
                  onTaskPreview={() =>
                    navigateToPage("taskDetails", {
                      taskId: customerTask.id,
                    })
                  }
                  className={cx("shrink-0", idx !== 0 && "mt-4")}
                  {...props}
                />
              )}
            </CustomerTaskCardProvider>
          ))}
        </VList>
      ) : (
        <div className="flex h-full items-center justify-center">
          <span className="px-6 text-center font-secondary text-[13px] leading-[150%] text-[#828282]">
            {t({
              id: "client-support-page.no-tasks-related-to-customer",
              defaultMessage: "No Tasks related to this Customer",
            })}
          </span>
        </div>
      )}
    </DataLoadingWrapper>
  );
};
